import {
  FORMATION_GAME_SIZE,
  FORMATION_LIMITS,
  FORMATION_ROLE,
  FORMATION_ROLE_LABELS,
  type FormationGameSize,
  type FormationInput,
  type FormationPosition,
  type FormationRole,
  type TrainingFormationDto,
} from '@ukinfo/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { ApiError, api } from '../../lib/api';
import { NEW_FORMATION_POSITIONS, gameSizeLabel, nextFreeSpot } from '../../lib/formations';
import { useUnsavedChanges } from '../../lib/useUnsavedChanges';
import { Button, Dialog, Field, Input, Select, Textarea, cn, useToast } from '../ui';
import { FormationEditorPitch } from './FormationPitch';

/**
 * Formular fuer eine eigene Formation: Name, Spielform, Beschreibung und die Spieler auf dem Feld.
 *
 * Spieler werden auf dem Feld gezogen (Zeiger) oder ueber die Liste darunter markiert; dort aendert sich
 * auch ihr Kuerzel, und sie lassen sich entfernen. Neue Spieler kommen bis zur Zahl der Spielform dazu.
 * Wird nur gemountet, solange es offen ist: Jede Oeffnung beginnt mit frischem Stand.
 */

interface Draft {
  name: string;
  gameSize: FormationGameSize;
  description: string;
  positions: FormationPosition[];
}

function draftFrom(formation: TrainingFormationDto | null): Draft {
  return {
    name: formation?.name ?? '',
    gameSize: formation?.gameSize ?? 11,
    description: formation?.description ?? '',
    positions: formation?.positions ?? NEW_FORMATION_POSITIONS,
  };
}

const toInput = (draft: Draft): FormationInput => ({
  name: draft.name.trim(),
  gameSize: draft.gameSize,
  description: draft.description.trim(),
  positions: draft.positions.map((position) => ({ ...position, label: position.label.trim() })),
});

export function FormationFormDialog({
  formation,
  onClose,
  onSaved,
}: {
  /** Vorhandene eigene Formation zum Bearbeiten; null = neu. */
  formation: TrainingFormationDto | null;
  onClose: () => void;
  onSaved?: (saved: TrainingFormationDto) => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [initial] = useState(() => draftFrom(formation));
  const [draft, setDraft] = useState<Draft>(initial);
  const [selected, setSelected] = useState<number | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const set = (patch: Partial<Draft>): void => setDraft((current) => ({ ...current, ...patch }));
  const { positions } = draft;
  const current = selected !== null ? positions[selected] : undefined;

  const save = useMutation({
    mutationFn: () => {
      const input = toInput(draft);
      return formation
        ? api.patch<TrainingFormationDto>(`/api/training/formations/${formation.id}`, input)
        : api.post<TrainingFormationDto>('/api/training/formations', input);
    },
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: ['training-formations'] });
      await queryClient.invalidateQueries({ queryKey: ['training-formation'] });
      toast.show(formation ? 'Formation gespeichert' : 'Formation angelegt');
      onSaved?.(saved);
      onClose();
    },
    onError: (error: unknown) => {
      if (error instanceof ApiError) {
        setErrors(error.fieldErrors());
        toast.show(error.message, 'error');
      } else {
        toast.show('Speichern fehlgeschlagen', 'error');
      }
    },
  });

  const dirty = JSON.stringify(draft) !== JSON.stringify(initial);
  const requestClose = useUnsavedChanges(true, dirty, onClose, save.isPending);

  const submit = (): void => {
    const found: Record<string, string> = {};
    if (!draft.name.trim()) found['name'] = 'Bitte gib einen Namen an.';
    if (positions.length === 0) found['positions'] = 'Mindestens ein Spieler gehört zur Formation.';
    if (positions.some((position) => !position.label.trim())) found['positions'] = 'Jeder Spieler braucht ein Kürzel.';
    setErrors(found);
    if (Object.keys(found).length === 0) save.mutate();
  };

  const addPlayer = (): void => {
    if (positions.length >= draft.gameSize) return;
    const spot = nextFreeSpot(positions);
    set({ positions: [...positions, { ...spot, label: 'ZM', role: 'MID' }] });
    setSelected(positions.length);
  };

  const removePlayer = (): void => {
    if (selected === null) return;
    set({ positions: positions.filter((_, index) => index !== selected) });
    setSelected(null);
  };

  const change = (patch: Partial<FormationPosition>): void => {
    if (selected === null) return;
    set({ positions: positions.map((position, index) => (index === selected ? { ...position, ...patch } : position)) });
  };

  const changeRole = (value: string): void => {
    if (selected === null) return;
    set({
      positions: positions.map((position, index) => {
        if (index !== selected) return position;
        const { role: _alt, ...rest } = position;
        return value ? { ...rest, role: value as FormationRole } : rest;
      }),
    });
  };

  return (
    <Dialog
      open
      onClose={requestClose}
      title={formation ? 'Formation bearbeiten' : 'Neue Formation'}
      wide
      footer={
        <>
          <Button variant="secondary" onClick={requestClose} disabled={save.isPending}>Abbrechen</Button>
          <Button onClick={submit} loading={save.isPending}>Speichern</Button>
        </>
      }
    >
      <fieldset disabled={save.isPending} className="min-w-0 space-y-4">
        {Object.keys(errors).length > 0 && (
          <p role="alert" className="rounded-lg bg-rose-50 p-3 text-sm text-rose-800">Bitte prüfe die markierten Angaben. Deine Eingaben bleiben erhalten.</p>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Name" required error={errors['name']}>
            {(id) => <Input id={id} value={draft.name} maxLength={FORMATION_LIMITS.name} onChange={(event) => set({ name: event.target.value })} autoFocus />}
          </Field>
          <Field label="Spielform" required error={errors['gameSize']} hint="Bestimmt, wie viele Spieler höchstens auf dem Feld stehen.">
            {(id) => (
              <Select id={id} value={String(draft.gameSize)} onChange={(event) => set({ gameSize: Number(event.target.value) as FormationGameSize })}>
                {FORMATION_GAME_SIZE.map((size) => (
                  <option key={size} value={size} disabled={size < positions.length}>{gameSizeLabel(size)}</option>
                ))}
              </Select>
            )}
          </Field>
        </div>

        <Field label="Beschreibung" error={errors['description']} hint="Wofür sich die Aufstellung eignet.">
          {(id) => <Textarea id={id} rows={2} value={draft.description} maxLength={FORMATION_LIMITS.description} onChange={(event) => set({ description: event.target.value })} />}
        </Field>

        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="label">Spieler auf dem Feld</span>
            <span className={cn('text-xs tabular-nums', positions.length >= draft.gameSize ? 'font-medium text-amber-700' : 'text-slate-500')}>
              {positions.length} von {draft.gameSize} Spielern
            </span>
          </div>
          <p className="text-xs text-slate-500">
            Spieler mit dem Finger oder der Maus an die gewünschte Stelle ziehen. Eigenes Tor unten, Spielrichtung nach oben.
          </p>
          <FormationEditorPitch positions={positions} selected={selected} onSelect={setSelected} onChange={(next) => set({ positions: next })} disabled={save.isPending} />
          {errors['positions'] && <p className="error-text">{errors['positions']}</p>}

          <ul aria-label="Spieler" className="flex flex-wrap gap-1.5">
            {positions.map((position, index) => (
              <li key={index}>
                <button
                  type="button"
                  aria-label={`Spieler ${index + 1}: ${position.label}`}
                  aria-pressed={selected === index}
                  onClick={() => setSelected(selected === index ? null : index)}
                  className={cn(
                    'min-h-11 min-w-11 rounded-xl border px-2 text-sm font-semibold shadow-sm transition-colors sm:min-h-0 sm:py-1',
                    selected === index ? 'border-brand-600 bg-brand-600 text-white' : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50',
                  )}
                >
                  {position.label || '?'}
                </button>
              </li>
            ))}
          </ul>

          {current && (
            <div className="grid grid-cols-2 items-end gap-3 rounded-xl bg-slate-50 p-3 ring-1 ring-slate-200 sm:grid-cols-[1fr_1fr_auto]">
              <Field label="Kürzel" hint="Bis zu 4 Zeichen, z. B. IV oder 10">
                {(id) => <Input id={id} value={current.label} maxLength={FORMATION_LIMITS.label} onChange={(event) => change({ label: event.target.value })} />}
              </Field>
              <Field label="Aufgabe">
                {(id) => (
                  <Select id={id} value={current.role ?? ''} onChange={(event) => changeRole(event.target.value)}>
                    <option value="">–</option>
                    {FORMATION_ROLE.map((role) => (
                      <option key={role} value={role}>{FORMATION_ROLE_LABELS[role]}</option>
                    ))}
                  </Select>
                )}
              </Field>
              <Button type="button" variant="secondary" className="col-span-2 sm:col-span-1" onClick={removePlayer}>
                Spieler entfernen
              </Button>
            </div>
          )}

          <Button type="button" variant="secondary" disabled={positions.length >= draft.gameSize} onClick={addPlayer}>
            + Spieler hinzufügen
          </Button>
        </div>
      </fieldset>
    </Dialog>
  );
}
